import type { api } from '@agency/shared';

/** Most recent assignment target, retained for historical attribution. */
export interface KeyAttribution {
  scope: api.AiKeyAssignmentScope;
  /** Null for an organization-wide (shared) attribution. */
  userId: api.User['id'] | null;
  userDisplayName: string | null;
}

export type KeyDisposition = 'assigned' | 'spare' | 'retired' | 'inactive';

type LedgerEntry = Pick<api.AiKeyAssignment, 'aiKeyId' | 'scope' | 'userId' | 'validFrom'> & { userDisplayName: string | null };
type KeyState = Pick<api.AiKey, 'status' | 'assignedUserId' | 'assignmentScope'>;

/** Most recent ledger entry per key. Order independent. */
export function attributionByKey(ledger: readonly LedgerEntry[]): Map<string, KeyAttribution> {
  const latest = new Map<string, LedgerEntry>();
  for (const entry of ledger) {
    const current = latest.get(entry.aiKeyId);
    if (!current || entry.validFrom > current.validFrom) latest.set(entry.aiKeyId, entry);
  }
  const out = new Map<string, KeyAttribution>();
  for (const [keyId, entry] of latest) out.set(keyId, { scope: entry.scope, userId: entry.userId, userDisplayName: entry.userDisplayName });
  return out;
}

export function keyDisposition(key: KeyState, attribution: KeyAttribution | null | undefined): KeyDisposition {
  if (key.status !== 'active') return 'inactive';
  if (key.assignmentScope || key.assignedUserId) return 'assigned';
  return attribution ? 'retired' : 'spare';
}

/** Short owner wording for an attribution ("all users" or the person's name). */
export function attributionLabel(attribution: KeyAttribution): string {
  return attribution.scope === 'organization' ? 'all users' : attribution.userDisplayName ?? 'a former user';
}

export interface KeyCounts {
  assigned: number;
  spare: number;
  retired: number;
}

export function countDispositions(keys: readonly (Pick<api.AiKey, 'id'> & KeyState)[], attribution: ReadonlyMap<string, KeyAttribution>): KeyCounts {
  const counts: KeyCounts = { assigned: 0, spare: 0, retired: 0 };
  for (const key of keys) {
    const d = keyDisposition(key, attribution.get(key.id));
    if (d === 'assigned') counts.assigned += 1;
    else if (d === 'spare') counts.spare += 1;
    else if (d === 'retired') counts.retired += 1;
  }
  return counts;
}
